import React from 'react';

interface VirtualizedTableProps {
  columns: string[];
  data: any[];
  formatCellValue?: (value: any) => string;
  formatHeader?: (column: string) => string;
  maxCellLength?: number;
  rowHeight?: number;
  overscan?: number;
  containerClassName?: string;
  tableClassName?: string;
  theadClassName?: string;
  headerCellClassName?: string;
  cellClassName?: string;
  rowClassName?: string;
  minRows?: number; // Nueva: altura mínima en filas
  maxHeight?: number; // Nueva: altura máxima del contenedor
  disableVerticalScroll?: boolean;
  enableSorting?: boolean;
}

type SortState = {
  column: string | null;
  direction: 'asc' | 'desc' | 'none';
};

const compareValues = (a: any, b: any) => {
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  const parsedA = Number(a);
  const parsedB = Number(b);
  if (!Number.isNaN(parsedA) && !Number.isNaN(parsedB)) {
    return parsedA - parsedB;
  }
  return String(a ?? '').localeCompare(String(b ?? ''), 'es', { numeric: true });
};

const VirtualizedTable: React.FC<VirtualizedTableProps> = React.memo(({
  columns,
  data,
  formatCellValue = (value: any) => String(value ?? ''),
  formatHeader = (column: string) => column,
  maxCellLength = 60,
  rowHeight = 36,
  overscan = 10,
  containerClassName,
  tableClassName,
  theadClassName,
  headerCellClassName,
  cellClassName,
  rowClassName,
  minRows = 2,
  maxHeight = 400,
  disableVerticalScroll = false,
  enableSorting = true,
}) => {
  const [sortState, setSortState] = React.useState<SortState>({ column: null, direction: 'none' });

  React.useEffect(() => {
    if (sortState.column && !columns.includes(sortState.column)) {
      setSortState({ column: null, direction: 'none' });
    }
  }, [columns, sortState.column]);

  const renderCell = React.useCallback((value: any) => {
    const text = formatCellValue(value);
    if (!maxCellLength || text.length <= maxCellLength) {
      return text;
    }
    return `${text.slice(0, maxCellLength)}…`;
  }, [formatCellValue, maxCellLength]);

  const sortedData = React.useMemo(() => {
    if (!enableSorting || !sortState.column || sortState.direction === 'none') {
      return data;
    }
    const next = [...data];
    next.sort((a, b) => {
      const valueA = a?.[sortState.column as string];
      const valueB = b?.[sortState.column as string];
      const comparison = compareValues(valueA, valueB);
      return sortState.direction === 'asc' ? comparison : -comparison;
    });
    return next;
  }, [data, enableSorting, sortState.column, sortState.direction]);

  const handleSortClick = (column: string) => {
    if (!enableSorting) return;
    setSortState((prev) => {
      if (prev.column === column) {
        if (prev.direction === 'asc') {
          return { column, direction: 'desc' };
        }
        if (prev.direction === 'desc') {
          return { column: null, direction: 'none' };
        }
        return { column, direction: 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const handleControlPointerDown = (event: React.MouseEvent | React.PointerEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const containerStyle: React.CSSProperties = disableVerticalScroll
    ? { overflow: 'visible', position: 'relative' }
    : { maxHeight: `${maxHeight}px`, overflow: 'auto', position: 'relative' };

  return (
    <div
      className={containerClassName}
      style={containerStyle}
    >
      <table className={`${tableClassName} w-full`} style={{ borderCollapse: 'collapse' }}>
        <thead className={`${theadClassName} sticky top-0 z-10 bg-slate-900`}>
          <tr>
            {columns.map((col) => (
              <th key={col} title={col} className={`${headerCellClassName} px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider border-b border-slate-700`}>
                {enableSorting ? (
                  <div className="flex items-center gap-2">
                    <span>{formatHeader(col)}</span>
                    <button
                      type="button"
                      onClick={() => handleSortClick(col)}
                      onMouseDown={handleControlPointerDown}
                      onPointerDown={handleControlPointerDown}
                      aria-label={`Ordenar ${formatHeader(col)}: ${
                        sortState.column === col
                          ? sortState.direction === 'asc'
                            ? 'descendente'
                            : sortState.direction === 'desc'
                              ? 'restaurar'
                              : 'ascendente'
                          : 'ascendente'
                      }`}
                      className="table-sort-button flex items-center justify-center text-[10px] text-slate-400 hover:text-sky-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 focus-visible:outline-offset-2"
                    >
                      {sortState.column === col ? (
                        <span className="text-sky-400">
                          {sortState.direction === 'asc' ? '▲' : sortState.direction === 'desc' ? '▼' : '↕'}
                        </span>
                      ) : (
                        <span>↕</span>
                      )}
                    </button>
                  </div>
                ) : (
                  formatHeader(col)
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-12 text-center text-slate-400" style={{ height: `${rowHeight * 2}px` }}>
                No hay datos para mostrar
              </td>
            </tr>
          ) : (
            sortedData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                style={{ height: `${rowHeight}px` }}
                className={`${rowClassName} hover:bg-slate-800 border-b border-slate-700`}
              >
                {columns.map((col) => {
                  const value = row?.[col];
                  const text = renderCell(value);
                  return (
                    <td 
                      key={col} 
                      title={String(value ?? '')} 
                      className={`${cellClassName} px-4 py-3 text-sm text-slate-300`}
                    >
                      {text}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
});

export default VirtualizedTable;
